import styles from './LogoLoader.module.css';
import { cn } from '@/lib/utils';

const SIZES = {
  sm: { width: 24, height: 27 },
  md: { width: 56, height: 62 },
  lg: { width: 80, height: 88 },
} as const;

const COLORS = {
  dark: '#240115',
  light: '#ffffff',
  brand: '#534AB7',
} as const;

interface LogoLoaderProps {
  size?: keyof typeof SIZES;
  color?: keyof typeof COLORS;
  className?: string;
}

const PATHS = [
  {
    d: 'M 27.553,5.573 A 2.081100730382842,2.081100730382842 0.0 0,1 29.642,9.173 L 18.036,15.905999999999999 C 16.455000000000002,16.907999999999998 14.347000000000001,16.038999999999998 13.931000000000001,14.206999999999999 L 13.881,13.988999999999999 L 13.825000000000001,7.778999999999999 A 2.081,2.081 0.0 0,1 17.986,7.740999999999999 L 18.017,11.104999999999999 L 27.553,5.573',
    origin: '21.73px 10.74px',
    begin: '0.0s',
  },
  {
    d: 'M 29.509,10.834999999999994 A 2.0811045144345823,2.0811045144345823 0.0 1,1 31.549,14.462999999999994 L 21.209,20.274999999999995 L 26.551,24.667999999999996 A 2.081,2.081 0.0 1,1 23.907999999999998,27.882999999999996 L 16.938,22.152999999999995 L 16.936999999999998,22.150999999999996 C 15.460999999999999,20.935999999999996 15.664999999999997,18.611999999999995 17.339,17.676999999999996 L 29.509,10.834999999999994',
    origin: '24.24px 19.36px',
    begin: '-0.4s',
  },
  {
    d: 'M 24.953,35.876999999999995 A 2.081,2.081 0.0 1,0 27.579,32.648999999999994 L 17.174,24.18 C 15.773,22.945 13.564,23.466 12.858999999999998,25.197 L 10.398,31.2 A 2.081,2.081 0.0 0,0 14.248,32.778 L 15.974,28.57 L 24.953,35.876999999999995',
    origin: '18.99px 30.03px',
    begin: '-0.8s',
  },
  {
    d: 'M 10.153,28.794 A 2.081145417792808,2.081145417792808 0.0 1,1 6.324,27.162 L 7.3229999999999995,24.822 L 5.2589999999999995,24.666999999999998 A 2.081,2.081 0.0 0,1 5.568999999999999,20.516999999999996 L 9.91,20.840999999999998 L 9.915000000000001,20.840999999999998 A 2.3,2.3 0.0 0,1 11.530000000000001,21.711 C 11.792000000000002,22.040999999999997 11.928,22.386 12.000000000000002,22.639 C 12.148000000000001,23.15 12.167000000000002,23.727 12.043000000000001,24.274 L 12.002,24.457 L 10.153,28.794',
    origin: '8.65px 24.66px',
    begin: '-1.2s',
  },
  {
    d: 'M 5.855,19.422 A 2.081,2.081 0.0 0,1 6.235,15.278 L 8.525,15.488000000000001 L 8.16,4.465 A 2.0811441564677833,2.0811441564677833 0.0 1,1 12.32,4.327 L 12.728,16.644 C 13.008,18.358999999999998 11.594,19.947 9.793,19.782999999999998 L 5.855,19.422',
    origin: '9.29px 12.05px',
    begin: '-1.6s',
  },
] as const;

export function LogoLoader({ size = 'md', color = 'dark', className }: LogoLoaderProps) {
  const { width, height } = SIZES[size];
  const fill = COLORS[color];

  return (
    <span className={cn('inline-flex items-center justify-center', className)} aria-hidden="true">
      <svg
        className={styles.logo}
        width={width}
        height={height}
        viewBox="0 0 37 41"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {PATHS.map(({ d, origin, begin }, index) => (
          <path
            key={index}
            d={d}
            fill={fill}
            style={{ transformBox: 'fill-box', transformOrigin: origin }}
          >
            <animate
              attributeName="opacity"
              values="0.4;1.0;0.4"
              dur="4.0s"
              begin={begin}
              repeatCount="indefinite"
              calcMode="spline"
              keyTimes="0;0.5;1"
              keySplines="0.37 0 0.63 1;0.37 0 0.63 1"
            />
            <animateTransform
              attributeName="transform"
              type="scale"
              values="0.97;1.03;0.97"
              dur="4.0s"
              begin={begin}
              repeatCount="indefinite"
              additive="sum"
              calcMode="spline"
              keyTimes="0;0.5;1"
              keySplines="0.37 0 0.63 1;0.37 0 0.63 1"
            />
          </path>
        ))}
      </svg>
    </span>
  );
}
